import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./toastCustom.css";
import "./App.css";

import { AuthProvider } from "./contexts/AuthContext";

import Home from "./pages/home";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import RegisterOrganisationPage from "./pages/RegisterOrganisationPage";
import Dashboard from "./pages/Dashboard";
import Uploads from "./pages/Uploads";
import ProtectedRoute from "./components/ProtectedRoute";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import CancelDeletion from "./pages/CancelDeletion";
import DeletionRequested from "./pages/DeletionRequested";
import LearnMore from "./pages/LearnMore";
import HowItWorks from "./pages/HowItWorks";
import Features from "./pages/Features";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
// Ticketing Dashboard Pages
import TicketWizard from "./pages/ticketing/TicketWizard";
import TicketPurchase from "./pages/ticketing/TicketPurchase";
import CheckoutStart from "./pages/ticketing/CheckoutStart";
import TicketingPortal from "./pages/ticketing/TicketingPortal";
import TicketingManage from "./pages/ticketing/TicketingManage";
import TicketingStats from "./pages/ticketing/TicketingStats";
import TicketingSettings from "./pages/ticketing/TicketingSettings";

// Instructor Dashboard Pages
import InstructorProfilePage from "./pages/instructor/InstructorProfilePage";
import BlockDatesPage from "./pages/instructor/BlockDatesPage";
import InstructorDashboard from "./pages/instructor/InstructorDashboard";
import ManageAvailabilityUnified from "./pages/instructor/ManageAvailabilityUnified";
import FinancesDashboard from "./pages/FinancesDashboard";
import ReconcilePayments from "./pages/ReconcilePayments";
import InstructorPublicProfile from "./pages/InstructorPublicProfile";
import InstructorSearch from "./pages/InstructorSearch";
import FindEvents from "./pages/FindEvents";
import BookingConfirmation from "./pages/BookingConfirmation";
import BookingSuccess from "./pages/BookingSuccess";
import BookingPayment from "./pages/booking/BookingPayment";
import GuestBookingPayment from "./pages/booking/GuestBookingPayment";
import BookingMagicLinkPaymentPage from "./pages/booking/BookingMagicLinkPaymentPage";
import TipPaymentPage from "./pages/TipPaymentPage";
import BookingStudentsPage from "./pages/bookings/BookingStudentsPage";
import MyHub from "./pages/MyHub";

// Class Management Pages
import ClassesPortal from "./pages/classes/ClassesPortal";
import ClassesSetupGate from "./pages/classes/ClassesSetupGate";
import ManageClassesPortal from "./pages/classes/ManageClassesPortal";
import ClassCheckIn from "./pages/classes/ClassCheckIn";
import ClassTermsPage from "./pages/classes/ClassTermsPage";
import ClassDefinitionsPage from "./pages/classes/ClassDefinitionsPage";
import ClassStudentsPage from "./pages/classes/ClassStudentsPage";
import ClassStudentImportPage from "./pages/classes/ClassStudentImportPage";
import ClassSettingsPage from "./pages/classes/ClassSettingsPage";
import ClassInstructorsPage from "./pages/classes/ClassInstructorsPage";
import ShortCourseDetailPage from "./pages/classes/ShortCourseDetailPage";
import ShortCourseGroupPage from "./pages/classes/ShortCourseGroupPage";
// Class Management Public Pages
import PublicSchool from "./pages/classes/public/PublicSchool";
import PublicGroupPage from "./pages/classes/public/PublicGroupPage";
import PublicEnquirePage from "./pages/classes/public/PublicEnquirePage";
import ShortCoursePage from "./pages/classes/public/ShortCoursePage";
import TrialDealLandingPage from "./pages/classes/public/TrialDealLandingPage";
import ClassPaymentPage from "./pages/classes/public/ClassPaymentPage";
import ConfirmEnrolmentPage from "./pages/classes/public/ConfirmEnrolmentPage";
import StudentJoinPage from "./pages/classes/public/StudentJoinPage";
import SchoolsDirectory from "./pages/SchoolsDirectory";
// Class Management Admin Pages (Phase 2)
import ClassPricingPage from "./pages/classes/ClassPricingPage";

// Timetable Dashboard Pages
import TimetablePortal from "./pages/timetables/TimetablePortal";
import TimetableCreate from "./pages/timetables/TimetableCreate";
import TimetableManage from "./pages/timetables/TimetableManage";

import Settings from "./pages/Settings";
import CommunicationsPage from "./pages/CommunicationsPage";
import Reports from "./pages/Reports";
import ManageFinances from "./pages/ManageFinances";
import HelpPage from "./pages/HelpPage";
import HelpGettingStarted from "./pages/help/HelpGettingStarted";
import HelpClasses from "./pages/help/HelpClasses";
import HelpPrivateLessons from "./pages/help/HelpPrivateLessons";
import HelpTicketing from "./pages/help/HelpTicketing";
import HelpStudents from "./pages/help/HelpStudents";
import HelpFinances from "./pages/help/HelpFinances";
import MyHubHelp from "./pages/help/MyHubHelp";

// Demo/Showcase Pages (developer accounts only — gated by DevProtectedRoute)
import DevProtectedRoute from "./components/DevProtectedRoute";
import DevPagesIndex from "./pages/DevPagesIndex";
import FormInputShowcase from "./pages/FormInputShowcase";
import TestModals from "./pages/TestModals";
import EmailCopyShowcase from "./pages/EmailCopyShowcase";
import CompleteProfilePage from "./pages/CompleteProfilePage";

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/dance-school-connect" element={<LoginPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    {/* Standalone org-setup page — OUTSIDE ProtectedRoute: arriving from RegisterPage
                        step 1, AuthContext has not re-read the just-stored token yet, so the page hydrates
                        the user itself (and sends a genuinely logged-out visitor to /register). */}
                    <Route path="/register/organisation" element={<RegisterOrganisationPage />} />
                    <Route path="/complete-profile" element={<CompleteProfilePage />} />
                    <Route path="/learn-more" element={<LearnMore />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/features" element={<Features />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-conditions" element={<TermsConditions />} />
                    <Route path="/tickets" element={<TicketPurchase />} />
                    <Route path="/checkout/start" element={<CheckoutStart />} />

                    {/* Instructor Search & Public Profiles */}
                    <Route path="/instructors" element={<InstructorSearch />} />

                    {/* Event Discovery (bare /events only — /events/:id is the
                        backend-rendered landing page via proxy, NOT an SPA route) */}
                    <Route path="/events" element={<FindEvents />} />
                    <Route path="/instructor/:id" element={<InstructorPublicProfile />} />

                    {/* Booking Flow Pages */}
                    <Route path="/booking-confirm/:instructorId" element={<BookingConfirmation />} />
                    <Route path="/booking-success" element={<BookingSuccess />} />
                    <Route path="/booking/payment" element={<BookingPayment />} />
                    <Route path="/booking-payment-guest" element={<GuestBookingPayment />} />
                    <Route
                        path="/my-hub"
                        element={
                            <ProtectedRoute>
                                <MyHub />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/my-hub/help"
                        element={
                            <ProtectedRoute>
                                <MyHubHelp />
                            </ProtectedRoute>
                        }
                    />

                    {/* Demo/Showcase Pages — developer accounts only. /dev-pages is the
                        landing page linking out to each showcase. */}
                    <Route
                        path="/dev-pages"
                        element={
                            <DevProtectedRoute>
                                <DevPagesIndex />
                            </DevProtectedRoute>
                        }
                    />
                    <Route
                        path="/form-input-showcase"
                        element={
                            <DevProtectedRoute>
                                <FormInputShowcase />
                            </DevProtectedRoute>
                        }
                    />
                    <Route
                        path="/test-modals"
                        element={
                            <DevProtectedRoute>
                                <TestModals />
                            </DevProtectedRoute>
                        }
                    />
                    <Route
                        path="/email-copy-showcase"
                        element={
                            <DevProtectedRoute>
                                <EmailCopyShowcase />
                            </DevProtectedRoute>
                        }
                    />

                    {/* Redirect any unknown routes to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />

                    {/* Protected routes */}
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="viewDashboard">
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageSettings">
                                <Settings />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/uploads"
                        element={
                            <ProtectedRoute requiredRole="canUploadEvents">
                                <Uploads />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/create-tickets"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageTicketing">
                                <TicketWizard />
                            </ProtectedRoute>
                        }
                    />

                    {/* Instructor Dashboard Routes */}
                    <Route
                        path="/instructor-dashboard"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredFeature="privateLessons">
                                <InstructorDashboard />
                            </ProtectedRoute>
                        }
                    />
                    {/* Unified Availability Management - Phase 6 Complete */}
                    <Route
                        path="/manage-availability"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <ManageAvailabilityUnified />
                            </ProtectedRoute>
                        }
                    />
                    {/* Essential instructor feature pages (not part of availability refactor) */}
                    <Route
                        path="/instructor-profile-setup/profile"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <InstructorProfilePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/instructor-profile-setup/block-dates"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <BlockDatesPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/finances"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="viewFinancialReports">
                                <FinancesDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reconcile"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="viewFinancialReports">
                                <ReconcilePayments />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/bookings/students"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <BookingStudentsPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Ticketing Dashboard Routes */}
                    <Route
                        path="/ticketing"
                        element={
                            <ProtectedRoute
                                requiredRole="danceSchool"
                                requiredCapability="manageTicketing"
                                requiredFeature="ticketing">
                                <TicketingPortal />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ticketing/manage"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageTicketing">
                                <TicketingManage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ticketing/stats"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageTicketing">
                                <TicketingStats />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/ticketing/settings"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageTicketing">
                                <TicketingSettings />
                            </ProtectedRoute>
                        }
                    />

                    {/* Class Management Routes (public — no auth required) */}
                    <Route path="/schools" element={<SchoolsDirectory />} />
                    <Route path="/school/:orgSlug" element={<PublicSchool />} />
                    <Route path="/school/:orgSlug/groups/:groupSlug" element={<PublicGroupPage />} />
                    <Route path="/school/:orgSlug/enquire" element={<PublicEnquirePage />} />
                    <Route path="/school/:orgSlug/course/:courseSlug" element={<ShortCoursePage />} />
                    <Route path="/trial-buy/:slug" element={<TrialDealLandingPage />} />
                    <Route path="/class-payment/:qrToken" element={<ClassPaymentPage />} />
                    <Route path="/booking-payment/:qrToken" element={<BookingMagicLinkPaymentPage />} />
                    <Route path="/tip/:token" element={<TipPaymentPage />} />
                    <Route path="/confirm-enrolment/:token" element={<ConfirmEnrolmentPage />} />
                    <Route path="/join" element={<StudentJoinPage />} />

                    {/* Class Management Routes (protected) */}
                    <Route
                        path="/classes"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredFeature="classManagement">
                                <ClassesSetupGate>
                                    <ClassesPortal />
                                </ClassesSetupGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/manage"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ClassesSetupGate>
                                    <ManageClassesPortal />
                                </ClassesSetupGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/checkin"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="checkIn">
                                <ClassCheckIn />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/terms"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ClassesSetupGate>
                                    <ClassTermsPage />
                                </ClassesSetupGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/short-courses/:id"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ShortCourseDetailPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/short-course-groups/:id"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ShortCourseGroupPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/terms/:termId/classes"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ClassDefinitionsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/students"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ClassesSetupGate>
                                    <ClassStudentsPage />
                                </ClassesSetupGate>
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/students/import"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ClassStudentImportPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/instructors"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ClassInstructorsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/promote"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ClassSettingsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/pricing"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <ClassPricingPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Timetable Dashboard Routes */}
                    <Route
                        path="/timetables"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <TimetablePortal />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/timetables/create"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <TimetableCreate />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/timetables/manage"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="manageClasses">
                                <TimetableManage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Communications */}
                    <Route
                        path="/communications"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredFeature="bulkEmail">
                                <CommunicationsPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* Reports, Manage Finances & Help Routes */}
                    <Route
                        path="/manage-finances"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="viewDashboard">
                                <ManageFinances />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/reports"
                        element={
                            <ProtectedRoute requiredRole="danceSchool" requiredCapability="viewAttendanceReports">
                                <Reports />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/help"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <HelpPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/help/getting-started"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <HelpGettingStarted />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/help/classes"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <HelpClasses />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/help/private-lessons"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <HelpPrivateLessons />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/help/ticketing"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <HelpTicketing />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/help/students"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <HelpStudents />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/help/contact" element={<Contact />} />
                    <Route
                        path="/help/finances"
                        element={
                            <ProtectedRoute requiredRole="danceSchool">
                                <HelpFinances />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/verify-email/:token" element={<VerifyEmail />} />
                    {/* Tokenless variant: reachable resend surface for users whose
                        verification email never arrived (no link to click) */}
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/cancel-deletion/:token" element={<CancelDeletion />} />
                    <Route path="/deletion-requested" element={<DeletionRequested />} />
                </Routes>
            </BrowserRouter>

            {/* Toast Notification Container */}
            <ToastContainer
                position="top-left"
                autoClose={2500}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </AuthProvider>
    );
}

export default App;
